import type React from 'react'
import { useRef, useState } from 'react'

type InputProps = React.InputHTMLAttributes<HTMLInputElement>

// React overrides the setter for `value` so we need to use
// `Object.getOwnPropertyDescriptor` to get the original setter
function setValue (input: HTMLInputElement, value: string) {
  const setValue = Object?.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value'
  )?.set

  setValue?.call(input, value)
}

export function useClearableState (
  defaultValue: InputProps['defaultValue'],
  value: InputProps['value'],
  onChange: InputProps['onChange']
) {
  const isUncontrolled = value === undefined

  const [isFilled, setFilled] = useState(isUncontrolled ? !!defaultValue : null)
  const ref = useRef<HTMLInputElement>(null)

  const clearValue = () => {
    if (ref.current) {
      setValue(ref.current, '')
      ref.current.focus()
      ref.current.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isUncontrolled) {
      setFilled(event.target.value !== '')
    }

    onChange?.(event)
  }

  const isClearable =
    (isUncontrolled && isFilled) || (!isUncontrolled && !!value && !!onChange)

  return { clearValue, handleChange, ref, isClearable }
}
